import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Stack,
  Box,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Collapse,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ShareIcon from '@mui/icons-material/Share';
import AppsIcon from '@mui/icons-material/Apps';
import BugReportIcon from '@mui/icons-material/BugReport';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AppShortcutIcon from '@mui/icons-material/AppShortcut';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ViewListIcon from '@mui/icons-material/ViewList';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import SearchIcon from '@mui/icons-material/Search';
import ManageHistoryRoundedIcon from '@mui/icons-material/ManageHistoryRounded';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { LogoutButton } from './LogoutButton';

// Single source of truth for the displayed App Manager version. Bump this
// string when cutting a new release; it renders as the badge next to the
// brand in both the desktop app bar and the mobile drawer.
const APP_VERSION = '2.0.0';

type LeafEntry = { kind: 'leaf'; to: string; label: string; icon: React.ReactElement };
type GroupEntry = {
  kind: 'group';
  id: string;
  label: string;
  icon: React.ReactElement;
  children: Array<{ to: string; label: string; description?: string; icon: React.ReactElement }>;
};
type NavEntry = LeafEntry | GroupEntry;

const NAV_ITEMS: NavEntry[] = [
  { kind: 'leaf', to: '/home', label: 'Home', icon: <HomeIcon fontSize="small" /> },
  {
    kind: 'group',
    id: 'deploy-center',
    label: 'Deployment',
    icon: <RocketLaunchIcon fontSize="small" />,
    children: [
      {
        to: '/home/deployment',
        label: 'App Deployment',
        description: 'Trigger builds and deploys for one or many apps',
        icon: <RocketLaunchIcon fontSize="small" />,
      },
      {
        to: '/home/deploy-scheduler',
        label: 'Deploy Scheduler',
        description: 'Schedule a deploy for a future time',
        icon: <ScheduleSendIcon fontSize="small" />,
      },
    ],
  },
  {
    kind: 'group',
    id: 'app-center',
    label: 'App Center',
    icon: <AppsIcon fontSize="small" />,
    children: [
      {
        to: '/home/app-distribution',
        label: 'App Distribution',
        description: 'Charts, version distribution, and fleet analytics',
        icon: <ShareIcon fontSize="small" />,
      },
      {
        to: '/home/apps',
        label: 'Config Readiness',
        description: 'Apps missing site key, bundle id, or deployment',
        icon: <ViewListIcon fontSize="small" />,
      },
    ],
  },
  {
    kind: 'group',
    id: 'build-info',
    label: 'Build Info',
    icon: <BugReportIcon fontSize="small" />,
    children: [
      {
        to: '/home/build-analytics',
        label: 'Build Analytics',
        description: 'Health, trends, heatmap, flaky-build detection',
        icon: <AnalyticsIcon fontSize="small" />,
      },
      {
        to: '/home/builds',
        label: 'Build Browser',
        description: 'Browse every build with full per-build details',
        icon: <ViewListIcon fontSize="small" />,
      },
      {
        to: '/home/build-cost',
        label: 'Build Duration',
        description: 'How long pipelines take per app and per day',
        icon: <AccessTimeIcon fontSize="small" />,
      },
      {
        to: '/home/error-logs',
        label: 'Error Logs',
        description: 'Detailed error inspection + inline log preview',
        icon: <ReceiptLongIcon fontSize="small" />,
      },
      {
        to: '/home/audit',
        label: 'Audit Log',
        description: 'Operator action stream — who did what, when',
        icon: <ManageHistoryRoundedIcon fontSize="small" />,
      },
    ],
  },
  {
    // "Help" dropdown — collects everything that documents the App Manager
    // platform itself (rather than the apps it ships). Release Notes used to
    // sit at the top level; moving it under Help makes the top nav less
    // crowded and keeps the two platform-meta links discoverable in one
    // place.
    kind: 'group',
    id: 'help',
    label: 'Help',
    icon: <HelpOutlineIcon fontSize="small" />,
    children: [
      {
        to: '/home/tutorial',
        label: 'Help & Tutorial',
        description: 'Walkthrough of every page, button, and workflow',
        icon: <HelpOutlineIcon fontSize="small" />,
      },
      {
        to: '/home/release-notes',
        label: 'Release Notes',
        description: 'App Manager platform release notes — what shipped, when',
        icon: <EditNoteIcon fontSize="small" />,
      },
    ],
  },
];

export const HeaderComponent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [menuAnchor, setMenuAnchor] = useState<{ [groupId: string]: HTMLElement | null }>({});

  const isLeafActive = (to: string) => {
    if (to === '/home') return location.pathname === '/home' || location.pathname === '/';
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const isGroupActive = (group: GroupEntry) => group.children.some(c => isLeafActive(c.to));

  const openGroupMenu = (groupId: string, target: HTMLElement) => {
    setMenuAnchor(prev => ({ ...prev, [groupId]: target }));
  };
  const closeGroupMenu = (groupId: string) => {
    setMenuAnchor(prev => ({ ...prev, [groupId]: null }));
  };

  const handleGroupNavigate = (groupId: string, to: string) => {
    closeGroupMenu(groupId);
    navigate(to);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, md: 68 }, px: { xs: 1.5, md: 3 }, gap: 2 }}>
          {/* Logo */}
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mr: { md: 4 } }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(99, 102, 241, 0.45)',
              }}
            >
              <AppShortcutIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.01em',
                color: '#fff',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              App Manager
            </Typography>
            <Box
              component="span"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                alignItems: 'center',
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                color: '#c4b5fd',
                bgcolor: 'rgba(139, 92, 246, 0.18)',
                border: '1px solid rgba(139, 92, 246, 0.35)',
                lineHeight: 1.4,
              }}
            >
              v{APP_VERSION}
            </Box>
          </Stack>

          {/* Desktop / tablet nav */}
          {!isMobile && (
            <Stack direction="row" spacing={isCompact ? 0.25 : 0.75} sx={{ flex: 1 }} data-tour="main-nav">
              {NAV_ITEMS.map((item) => {
                if (item.kind === 'leaf') {
                  const active = isLeafActive(item.to);
                  return (
                    <Tooltip key={item.to} title={isCompact ? item.label : ''} arrow>
                      <NavLink to={item.to} style={{ textDecoration: 'none' }}>
                        <PillButton active={active} icon={item.icon} label={item.label} isCompact={isCompact} />
                      </NavLink>
                    </Tooltip>
                  );
                }
                // Group
                const active = isGroupActive(item);
                const anchor = menuAnchor[item.id] || null;
                return (
                  <Box key={item.id}>
                    <Tooltip title={isCompact ? item.label : ''} arrow>
                      <Box
                        component="button"
                        onClick={(e) => openGroupMenu(item.id, e.currentTarget)}
                        sx={{
                          all: 'unset',
                          cursor: 'pointer',
                          display: 'inline-block',
                        }}
                        aria-haspopup="menu"
                        aria-expanded={Boolean(anchor)}
                      >
                        <PillButton
                          active={active}
                          icon={item.icon}
                          label={item.label}
                          isCompact={isCompact}
                          showCaret
                        />
                      </Box>
                    </Tooltip>
                    <Menu
                      anchorEl={anchor}
                      open={Boolean(anchor)}
                      onClose={() => closeGroupMenu(item.id)}
                      MenuListProps={{ dense: true, sx: { p: 0.5 } }}
                      PaperProps={{
                        elevation: 8,
                        sx: {
                          mt: 0.75,
                          minWidth: 280,
                          borderRadius: 2,
                          border: '1px solid rgba(99, 102, 241, 0.18)',
                        },
                      }}
                      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                    >
                      {item.children.map((child) => {
                        const childActive = isLeafActive(child.to);
                        return (
                          <MenuItem
                            key={child.to}
                            selected={childActive}
                            onClick={() => handleGroupNavigate(item.id, child.to)}
                            sx={{
                              alignItems: 'flex-start',
                              borderRadius: 1.25,
                              py: 1,
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(99, 102, 241, 0.12)',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: childActive ? 'primary.main' : 'text.secondary', mt: 0.25 }}>
                              {child.icon}
                            </ListItemIcon>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: childActive ? 'primary.main' : 'text.primary' }}>
                                {child.label}
                              </Typography>
                              {child.description && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}>
                                  {child.description}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </Box>
                );
              })}
            </Stack>
          )}

          {/* Mobile menu button */}
          {isMobile && (
            <Box sx={{ flex: 1 }}>
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: '#fff' }} aria-label="Open menu">
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* ⌘K command palette trigger — visible cue so users discover the
              shortcut. Dispatches a custom event the palette listens for so we
              don't have to thread state through the entire header tree. */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={`Open command palette (${navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl'}K)`} arrow>
              <Box
                data-tour="quick-search"
                onClick={() => window.dispatchEvent(new CustomEvent('fsam:open-palette'))}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5, py: 0.5,
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  color: 'rgba(255, 255, 255, 0.85)',
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  transition: 'background-color 120ms ease',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.16)' },
                }}
              >
                <SearchIcon sx={{ fontSize: 16 }} />
                <span>Quick search</span>
                <Box component="span" sx={{ ml: 0.5, px: 0.5, borderRadius: 0.75, bgcolor: 'rgba(255, 255, 255, 0.16)', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  {navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl K'}
                </Box>
              </Box>
            </Tooltip>
            <LogoutButton className="" />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer with the nav items */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)',
            color: '#fff',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 32, height: 32, borderRadius: 1.25,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <AppShortcutIcon sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff' }}>App Manager</Typography>
            <Box
              component="span"
              sx={{
                px: 0.75, py: 0.25, borderRadius: 1,
                fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.02em',
                color: '#c4b5fd', bgcolor: 'rgba(139, 92, 246, 0.18)',
                border: '1px solid rgba(139, 92, 246, 0.35)', lineHeight: 1.4,
              }}
            >
              v{APP_VERSION}
            </Box>
          </Stack>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        <List sx={{ p: 1 }}>
          {NAV_ITEMS.map((item) => {
            if (item.kind === 'leaf') {
              const active = isLeafActive(item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  onClick={() => setDrawerOpen(false)}
                >
                  <ListItemButton
                    sx={{
                      borderRadius: 2, mb: 0.5,
                      color: active ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                      bgcolor: active ? 'rgba(99, 102, 241, 0.18)' : 'transparent',
                      border: '1px solid',
                      borderColor: active ? 'rgba(139, 92, 246, 0.45)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} />
                  </ListItemButton>
                </NavLink>
              );
            }
            // Mobile group — expandable accordion
            const groupOpen = openGroups[item.id] ?? isGroupActive(item);
            return (
              <Box key={item.id} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => setOpenGroups(prev => ({ ...prev, [item.id]: !groupOpen }))}
                  sx={{
                    borderRadius: 2,
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem' }} />
                  {groupOpen ? <ExpandLessIcon sx={{ color: 'inherit' }} /> : <ExpandMoreIcon sx={{ color: 'inherit' }} />}
                </ListItemButton>
                <Collapse in={groupOpen} unmountOnExit>
                  <Box sx={{ pl: 2 }}>
                    {item.children.map((child) => {
                      const childActive = isLeafActive(child.to);
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                          onClick={() => setDrawerOpen(false)}
                        >
                          <ListItemButton
                            sx={{
                              borderRadius: 2, mt: 0.5, py: 0.75,
                              color: childActive ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                              bgcolor: childActive ? 'rgba(99, 102, 241, 0.18)' : 'transparent',
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>{child.icon}</ListItemIcon>
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem' }}
                              secondary={child.description}
                              secondaryTypographyProps={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}
                            />
                          </ListItemButton>
                        </NavLink>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Drawer>
    </>
  );
};

// Shared visual pill used by both leaf links and dropdown triggers.
const PillButton: React.FC<{
  active: boolean;
  icon: React.ReactElement;
  label: string;
  isCompact: boolean;
  showCaret?: boolean;
}> = ({ active, icon, label, isCompact, showCaret }) => (
  <Stack
    direction="row"
    spacing={0.75}
    alignItems="center"
    sx={{
      px: isCompact ? 1.25 : 1.75,
      py: 0.85,
      borderRadius: 2,
      color: active ? '#fff' : 'rgba(255, 255, 255, 0.65)',
      backgroundColor: active ? 'rgba(99, 102, 241, 0.18)' : 'transparent',
      border: '1px solid',
      borderColor: active ? 'rgba(139, 92, 246, 0.45)' : 'transparent',
      transition: 'all 150ms ease',
      '&:hover': {
        color: '#fff',
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
      },
    }}
  >
    {React.cloneElement(icon, {
      sx: { fontSize: 18, color: active ? '#a5b4fc' : 'inherit' },
    })}
    {!isCompact && (
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    )}
    {showCaret && <ArrowDropDownIcon sx={{ fontSize: 18, color: 'inherit', ml: -0.25 }} />}
  </Stack>
);
